import { getSupabaseClient } from '../../lib/supabase';
import type { AppUser, PinSessionResponse } from './types';
import type { OperationalUnitCode } from '../units/types';
import { InvalidUserSessionError, PinRateLimitError } from './authErrors';

const LOGIN_ERROR = 'Prijava ni uspela. Preverite PIN in poskusite znova.';

export async function submitPin(pin: string): Promise<PinSessionResponse> {
  const supabase = getSupabaseClient();
  // Supabase types the generic Edge Function transport as `any` internally.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data, error } = await supabase.functions.invoke<PinSessionResponse>(
    'pin-login',
    { body: { pin } },
  );

  const invokeError: unknown = error;
  if (
    invokeError &&
    typeof invokeError === 'object' &&
    'context' in invokeError &&
    invokeError.context instanceof Response &&
    invokeError.context.status === 429
  ) {
    throw new PinRateLimitError();
  }
  if (error || !data) throw new Error(LOGIN_ERROR);
  return data;
}

export async function applyPinSession(
  response: PinSessionResponse,
): Promise<string | null> {
  if (response.unlocked) return null;
  if (!response.session) throw new Error(LOGIN_ERROR);

  const { data, error } = await getSupabaseClient().auth.setSession({
    access_token: response.session.access_token,
    refresh_token: response.session.refresh_token,
  });
  if (error || !data.user) throw new Error(LOGIN_ERROR);
  return data.user.id;
}

export async function fetchCurrentUser(
  authUserId: string,
  signal?: AbortSignal,
): Promise<AppUser> {
  let query = getSupabaseClient().rpc('fn_my_profile');
  if (signal) query = query.abortSignal(signal);
  const { data, error } = await query.maybeSingle();

  if (error) {
    throw new Error('Profila ni bilo mogoče preveriti.');
  }

  if (
    !data?.is_active ||
    data.auth_user_id !== authUserId ||
    !data.profile_id ||
    !data.auth_user_id ||
    !data.display_name ||
    !data.role
  ) {
    throw new InvalidUserSessionError();
  }

  // During a staged deployment the frontend can briefly run against the
  // pre-operational-units profile shape. All data in that schema belongs to
  // Skofja Loka, so keep the existing WMS available until migration 047 lands.
  const unitCodes = Array.isArray(data.unit_codes)
    ? (data.unit_codes as OperationalUnitCode[])
    : (['SKOFJA_LOKA'] satisfies OperationalUnitCode[]);

  return {
    id: data.profile_id,
    authUserId: data.auth_user_id,
    displayName: data.display_name,
    role: data.role,
    isActive: data.is_active,
    canUseMetakocka: data.can_use_metakocka,
    sessionLockMinutes: data.session_lock_minutes,
    unitCodes,
  };
}
